import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Wifi, Zap, ShieldCheck, MapPin, Phone, CheckCircle2, Rocket,
  Lock, ArrowRight, Headphones, Clock, Globe, Star,
  Home, Building2, Gauge, Download, Film, Gamepad2, Video,
  Quote, Mail, Server, Network, Leaf, TreePine, X, Ban, CalendarX, TrendingDown
} from "lucide-react";
import AdminDashboard from "./AdminDashboard";
import OrderForm from "./OrderForm";
import CustomerPortal from "./CustomerPortal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// GeekBroadband Logo Component
const Logo = ({ className = "h-12" }) => (
  <img 
    src="/images/geekbroadband-logo.webp"
    alt="GeekBroadband"
    className={className}
    data-testid="brand-logo"
  />
);

// Official Guaranteed Irish Logos
const GuaranteedIrishLogo = ({ variant = "color", className = "h-12" }) => {
  const logos = {
    color: "/images/gi-logo-tagline.png",
    colorNoTag: "/images/gi-logo-no-tagline.png",
    white: "/images/gi-logo-tagline-white.png",
    whiteNoTag: "/images/gi-logo-no-tagline-white.png",
  };
  return (
    <img 
      src={logos[variant]}
      alt="Guaranteed Irish"
      className={className}
      data-testid="guaranteed-irish-logo"
    />
  );
};

// Network Provider Logos - Actual logos
const networkLogos = {
  openeir: "/images/openeir-white.png",
  siro: "/images/siro-logo.png",
  nbi: "/images/nbi-logo.png",
};

// Reforest Nation Logo
const reforestNationLogo = "/images/reforest-nation-logo.png";

const NetworkLogos = ({ inverted = false }) => (
  <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
    <img 
      src={networkLogos.openeir} 
      alt="open eir" 
      className={`h-10 md:h-12 object-contain ${inverted ? '' : 'invert'}`}
    />
    <img 
      src={networkLogos.siro} 
      alt="SIRO" 
      className={`h-8 md:h-10 object-contain ${inverted ? 'brightness-0 invert' : ''}`}
    />
    <img 
      src={networkLogos.nbi} 
      alt="NBI" 
      className={`h-10 md:h-12 object-contain ${inverted ? 'brightness-0 invert' : ''}`}
    />
  </div>
);

// Animated Speed Counter - counts up rapidly to show speed
const SpeedCounter = ({ targetSpeed = 5000, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * targetSpeed));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetSpeed, duration]);
  
  return (
    <span className="font-['JetBrains_Mono'] tabular-nums">{count.toLocaleString()}</span>
  );
};

// GOTCHA SPEED ELEMENT - Live Data Transfer Counter
const LiveDataCounter = () => {
  const [dataTransferred, setDataTransferred] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const gbPerSecond = 5 / 8; // 5 Gbps = 0.625 GB/s
  
  useEffect(() => {
    if (!isRunning) return;
    
    const interval = setInterval(() => {
      setDataTransferred(prev => prev + (gbPerSecond / 10)); // Update every 100ms
    }, 100);
    
    return () => clearInterval(interval);
  }, [isRunning]);
  
  return (
    <div className="text-center">
      <div className="mb-4">
        <span className="text-white/60 text-lg">Data transferred at 5Gbps:</span>
      </div>
      <div className="font-['JetBrains_Mono'] text-6xl md:text-8xl font-black text-[#F97530] mb-2">
        {dataTransferred.toFixed(1)} <span className="text-3xl md:text-4xl text-white">GB</span>
      </div>
      <button
        onClick={() => {
          if (isRunning) {
            setIsRunning(false);
            setDataTransferred(0);
          } else {
            setIsRunning(true);
          }
        }}
        className={`mt-4 px-8 py-3 rounded-full font-bold transition-all ${
          isRunning 
            ? 'bg-white/20 text-white hover:bg-white/30' 
            : 'bg-[#F97530] text-white hover:bg-[#e86620]'
        }`}
      >
        {isRunning ? 'Reset' : 'Start Live Demo'}
      </button>
    </div>
  );
};

// Speed Race Component - Visual comparison
const SpeedRace = () => {
  const [isRacing, setIsRacing] = useState(false);
  const [geekProgress, setGeekProgress] = useState(0);
  const [typicalProgress, setTypicalProgress] = useState(0);
  
  useEffect(() => {
    if (!isRacing) return;
    
    // 5Gbps vs 100Mbps = 50x faster
    const geekInterval = setInterval(() => {
      setGeekProgress(prev => {
        if (prev >= 100) {
          clearInterval(geekInterval);
          return 100;
        }
        return prev + 5;
      });
    }, 50);
    
    const typicalInterval = setInterval(() => {
      setTypicalProgress(prev => {
        if (prev >= 100) {
          clearInterval(typicalInterval);
          return 100;
        }
        return prev + 0.1;
      });
    }, 50);
    
    return () => {
      clearInterval(geekInterval);
      clearInterval(typicalInterval);
    };
  }, [isRacing]);
  
  const startRace = () => {
    setGeekProgress(0);
    setTypicalProgress(0);
    setIsRacing(true);
  };
  
  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8">
      <h3 className="text-white font-bold text-xl mb-6 text-center">
        Download Race: 25GB 4K Movie
      </h3>
      
      {/* GeekBroadband */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[#F97530] font-bold">GeekBroadband 5Gbps</span>
          <span className="text-white font-['JetBrains_Mono']">
            {geekProgress >= 100 ? '40 sec' : `${(geekProgress * 0.4).toFixed(0)} sec`}
          </span>
        </div>
        <div className="h-8 bg-white/10 rounded-full overflow-hidden relative">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#F97530] to-[#FF9F5B] rounded-full flex items-center justify-end pr-2"
            style={{ width: `${geekProgress}%` }}
          >
            {geekProgress >= 100 && (
              <span className="text-white text-sm font-bold">DONE!</span>
            )}
          </motion.div>
          {geekProgress > 0 && geekProgress < 100 && (
            <motion.div 
              className="absolute right-0 top-0 bottom-0 w-4 bg-white/50"
              style={{ left: `${geekProgress}%` }}
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 0.3, repeat: Infinity }}
            />
          )}
        </div>
      </div>
      
      {/* Typical Broadband */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/60 font-bold">Typical 100Mbps</span>
          <span className="text-white/60 font-['JetBrains_Mono']">
            {typicalProgress >= 100 ? '33 min' : `${(typicalProgress * 0.33).toFixed(0)} min`}
          </span>
        </div>
        <div className="h-8 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white/30 rounded-full transition-all duration-100"
            style={{ width: `${typicalProgress}%` }}
          />
        </div>
      </div>
      
      <button
        onClick={startRace}
        disabled={isRacing && geekProgress < 100}
        className="w-full bg-[#F97530] text-white py-4 rounded-full font-bold hover:bg-[#e86620] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Zap className="w-5 h-5" />
        {isRacing && geekProgress < 100 ? 'Racing...' : 'Start Race'}
      </button>
    </div>
  );
};

// Download Time Comparison Component
const DownloadComparison = () => {
  const comparisons = [
    { item: "4K Movie (25GB)", withGeek: "40 sec", typical: "33 min", icon: Film },
    { item: "Game Download (100GB)", withGeek: "2.5 min", typical: "2+ hours", icon: Gamepad2 },
    { item: "Video Call Upload", withGeek: "Crystal clear", typical: "Laggy", icon: Video },
  ];
  
  return (
    <div className="space-y-4">
      {comparisons.map((item, i) => (
        <motion.div
          key={item.item}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4"
        >
          <item.icon className="w-8 h-8 text-[#F97530]" />
          <div className="flex-1">
            <div className="text-white font-medium">{item.item}</div>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-[#F97530] font-bold font-['JetBrains_Mono']">{item.withGeek}</span>
              <span className="text-white/40 text-sm">vs {item.typical} typical</span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Data Stream Animation - particles flowing to show speed
const DataStream = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-[#F97530] rounded-full"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          x: [0, 100, 200],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: 1 + Math.random() * 0.5,
          repeat: Infinity,
          delay: Math.random() * 2,
          ease: "linear",
        }}
      />
    ))}
  </div>
);

// Home packages - Updated to 5Gbps
const homePackages = [
  { 
    name: "Essential", 
    speed: 500, 
    price: 45, 
    features: ["Perfect for browsing & streaming", "HD streaming on multiple devices", "Work from home ready"],
    popular: false 
  },
  { 
    name: "Family", 
    speed: 1000, 
    price: 55, 
    features: ["Whole family connected", "4K streaming on all TVs", "Online gaming & video calls", "Smart home ready"],
    popular: true 
  },
  { 
    name: "Gigabit Pro", 
    speed: 2000, 
    price: 75, 
    features: ["Ultra-fast for power users", "8K streaming ready", "Pro gaming & streaming", "Multiple heavy users"],
    popular: false 
  },
];

// Business packages - Updated to 5Gbps
const businessPackages = [
  { 
    name: "Business Starter", 
    speed: 1000, 
    price: 75, 
    features: ["Up to 15 employees", "Business-grade reliability", "Static IP included", "Priority phone support"],
    popular: false 
  },
  { 
    name: "Business Pro", 
    speed: 2000, 
    price: 99, 
    features: ["Up to 50 employees", "Enhanced SLA", "Multiple static IPs", "24/7 priority support"],
    popular: true 
  },
  { 
    name: "Business Ultra", 
    speed: 5000, 
    price: 149, 
    features: ["Unlimited employees", "Premium SLA guarantee", "Dedicated account manager", "Custom configurations"],
    popular: false 
  },
];

// Enterprise connectivity options
const enterpriseFeatures = [
  {
    title: "Dedicated Leased Lines",
    desc: "Symmetric speeds with guaranteed bandwidth. 1:1 contention ratio for mission-critical operations.",
    icon: Network,
  },
  {
    title: "Multi-Site Connectivity",
    desc: "Connect all your locations with secure, high-speed links. MPLS and SD-WAN solutions available.",
    icon: Globe,
  },
  {
    title: "Colocation & Data Centre",
    desc: "Host your infrastructure in Tier 3+ data centres with redundant connectivity.",
    icon: Server,
  },
];

// Customer testimonials
const testimonials = [
  {
    quote: "Switched from our previous provider and the difference is night and day. Downloads that took hours now take minutes. The local support team actually answers the phone!",
    name: "Michael O'Brien",
    role: "Owner, Clare Motor Factors",
    rating: 5,
  },
  {
    quote: "As a hotel, reliable internet is crucial for our guests. GeekBroadband delivers consistent speeds even during peak times. Highly recommend for hospitality businesses.",
    name: "Sarah Murphy",
    role: "Manager, Doonbeg Resort",
    rating: 5,
  },
  {
    quote: "Finally, a broadband provider that understands rural Ireland. We're getting speeds we never thought possible out here. The installation team were brilliant.",
    name: "Padraig Kelly",
    role: "Farmer & Business Owner",
    rating: 5,
  },
];

// Why Us features - Updated with no hidden fees messaging
const features = [
  {
    icon: Zap,
    title: "Up to 5Gbps",
    desc: "Blazing fast speeds that make buffering a thing of the past.",
    color: "orange"
  },
  {
    icon: Headphones,
    title: "Local Irish Support",
    desc: "Real people in Ireland, ready to help. No overseas call centres.",
    color: "navy"
  },
  {
    icon: ShieldCheck,
    title: "No Hidden Fees",
    desc: "What you see is what you pay. Honest pricing, always.",
    color: "orange"
  },
  {
    icon: Clock,
    title: "Quick Setup",
    desc: "Get connected fast. Most installations within 2 weeks.",
    color: "navy"
  },
];

// Promise badges - No contracts, no price hikes
const promiseBadges = [
  { icon: Ban, title: "No Hidden Fees", desc: "The price you see is the price you pay" },
  { icon: CalendarX, title: "No Long Contracts", desc: "Month-to-month flexibility" },
  { icon: TrendingDown, title: "No Yearly Price Hikes", desc: "Your rate stays the same" },
];

// Irish imagery
const irishImages = {
  hero: "https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=1920&q=80",
  aerial: "https://images.unsplash.com/photo-1741689944015-972d0db7dea2?w=1600&q=80",
  countryside: "https://images.unsplash.com/photo-1588417044404-ce4b91a74c7c?w=1600&q=80",
};

function MainSite() {
  const [eircode, setEircode] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [selectedType, setSelectedType] = useState("home");
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // Dynamic data from API
  const [packages, setPackages] = useState([]);
  const [apiTestimonials, setApiTestimonials] = useState([]);
  const [apiFaqs, setApiFaqs] = useState([]);
  const [banners, setBanners] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  // Order form state
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedPackageForOrder, setSelectedPackageForOrder] = useState(null);

  // Fetch data from API on mount and when selectedType changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch packages
        const packagesRes = await fetch(`${BACKEND_URL}/api/public/packages?package_type=${selectedType}`);
        if (packagesRes.ok) {
          const packagesData = await packagesRes.json();
          setPackages(packagesData);
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
      }
    };
    fetchData();
  }, [selectedType]);

  // Fetch other content once on mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [testimonialsRes, faqsRes, bannersRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/public/testimonials`),
          fetch(`${BACKEND_URL}/api/public/faqs`),
          fetch(`${BACKEND_URL}/api/public/banners`)
        ]);
        
        if (testimonialsRes.ok) {
          const testimonialsData = await testimonialsRes.json();
          setApiTestimonials(testimonialsData);
        }
        if (faqsRes.ok) {
          const faqsData = await faqsRes.json();
          setApiFaqs(faqsData);
        }
        if (bannersRes.ok) {
          const bannersData = await bannersRes.json();
          setBanners(bannersData);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleCheckAvailability = async () => {
    if (!eircode.trim()) return;
    
    setIsChecking(true);
    setAvailabilityError(null);
    setAvailabilityResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/check-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eircode: eircode.trim() }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to check availability');
      }
      
      setAvailabilityResult(data);
      setShowResults(true);
    } catch (error) {
      console.error('Availability check error:', error);
      setAvailabilityError(error.message || 'Unable to check availability. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  // Use API data for testimonials, falling back to hardcoded if empty
  const displayTestimonials = apiTestimonials.length > 0 ? apiTestimonials.map(t => ({
    quote: t.content,
    name: t.customer_name,
    role: t.package_name ? `${t.package_name} Customer` : t.location,
    rating: t.rating
  })) : testimonials;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Promotional Banner */}
      {banners.length > 0 && (
        <div 
          className="w-full py-3 px-4 text-center"
          style={{ backgroundColor: banners[0].background_color, color: banners[0].text_color }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 flex-wrap">
            <span className="font-semibold">{banners[0].title}</span>
            <span>{banners[0].content}</span>
            {banners[0].link_url && banners[0].link_text && (
              <a href={banners[0].link_url} className="underline font-semibold hover:opacity-80">
                {banners[0].link_text}
              </a>
            )}
          </div>
        </div>
      )}
      
      {/* Navigation with Home/Business distinction */}
      <nav className={`fixed ${banners.length > 0 ? 'top-[52px]' : 'top-0'} left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-100`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Logo className="h-10" />
            </motion.div>
            
            {/* Home/Business Navigation Links */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="hidden md:flex items-center gap-1 bg-slate-100 p-1 rounded-full"
            >
              <button
                data-testid="nav-home"
                onClick={() => setSelectedType("home")}
                className={`flex items-center gap-2 px-5 py-2 rounded-full font-semibold transition-all text-sm ${
                  selectedType === "home" 
                    ? "bg-[#1E3064] text-white" 
                    : "text-slate-600 hover:text-[#1E3064]"
                }`}
              >
                <Home className="w-4 h-4" />
                Home Broadband
              </button>
              <button
                data-testid="nav-business"
                onClick={() => setSelectedType("business")}
                className={`flex items-center gap-2 px-5 py-2 rounded-full font-semibold transition-all text-sm ${
                  selectedType === "business" 
                    ? "bg-[#1E3064] text-white" 
                    : "text-slate-600 hover:text-[#1E3064]"
                }`}
              >
                <Building2 className="w-4 h-4" />
                Business Broadband
              </button>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              <a 
                href="tel:+353656712772" 
                data-testid="nav-phone"
                className="hidden sm:flex items-center gap-2 text-[#1E3064] font-semibold"
              >
                <Phone className="w-4 h-4" />
                065 671 2772
              </a>
              <a 
                href="/portal"
                data-testid="nav-portal-btn"
                className="bg-[#F97530] text-white px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 hover:bg-[#e86620] transition-colors"
              >
                <Lock className="w-4 h-4" />
                My Account
              </a>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Bold Irish Background */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden">
        {/* Irish countryside background - MORE VISIBLE */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${irishImages.aerial}')`,
          }}
        />
        {/* Lighter gradient overlay to show more of the image */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/80 to-white/40" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Speed badge */}
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="inline-flex items-center gap-2 bg-[#1E3064] text-white px-4 py-2 rounded-full mb-6"
              >
                <Zap className="w-4 h-4 text-[#F97530]" />
                <span className="font-bold">Now up to 5Gbps!</span>
              </motion.div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight font-['Outfit'] text-[#1E3064] mb-6">
                {selectedType === "home" ? "Home" : "Business"} Fibre
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#F97530] to-[#FF9F5B]"> That Flies</span>
              </h1>
              
              <p className="text-lg md:text-xl leading-relaxed text-slate-700 mb-8 max-w-xl">
                {selectedType === "home" 
                  ? "Ireland's fastest residential broadband. Stream, game, and work from home without limits."
                  : "Enterprise-grade connectivity for Irish businesses. Built for speed, reliability, and growth."
                }
              </p>

              {/* Giant Speed Display */}
              <div className="mb-8">
                <div className="flex items-baseline gap-2">
                  <div className="text-7xl md:text-8xl font-black text-[#1E3064] font-['JetBrains_Mono']">
                    <SpeedCounter targetSpeed={5000} duration={2000} />
                  </div>
                  <div className="text-2xl font-bold text-[#F97530]">Mbps</div>
                </div>
                <div className="text-slate-500 font-medium mt-1">Maximum download speed</div>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#F97530]" />
                  <span className="font-semibold text-[#1E3064]">5x faster than typical fibre</span>
                </div>
              </div>
            </motion.div>

            {/* Right - Eircode Checker Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white shadow-2xl rounded-3xl p-8 md:p-10 border border-slate-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-r from-[#F97530] to-[#FF9F5B] p-3 rounded-xl">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-bold text-xl text-[#1E3064] font-['Outfit']">Check Your Speed</h2>
                    <p className="text-slate-500 text-sm">See what's available at your address</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="eircode-input"
                      placeholder="Enter your Eircode (e.g., V94 T9PX)"
                      value={eircode}
                      onChange={(e) => setEircode(e.target.value.toUpperCase())}
                      className="w-full px-6 py-5 pl-12 text-lg rounded-xl border-2 border-slate-200 focus:border-[#F97530] focus:ring-4 focus:ring-[#F97530]/10 outline-none transition-all font-['JetBrains_Mono'] bg-white"
                    />
                  </div>
                  
                  <button
                    data-testid="check-btn"
                    onClick={handleCheckAvailability}
                    disabled={isChecking}
                    className="w-full bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center gap-3 text-lg disabled:opacity-70"
                  >
                    {isChecking ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-5 h-5" />
                        Check Availability
                      </>
                    )}
                  </button>
                </div>

                <p className="text-slate-500 text-sm mt-4 text-center">
                  Don't know your Eircode? <a href="https://finder.eircode.ie" target="_blank" rel="noopener noreferrer" className="text-[#F97530] font-semibold hover:underline">Find it here →</a>
                </p>
                
                {/* Error message */}
                {availabilityError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {availabilityError}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Availability Results Modal */}
      <AnimatePresence>
        {showResults && availabilityResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowResults(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-[#1E3064] font-['Outfit']">Availability Results</h3>
                <button 
                  onClick={() => setShowResults(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-slate-500" />
                </button>
              </div>
              
              {/* Address */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="text-sm text-slate-500 mb-1">Address for {availabilityResult.eircode}</div>
                <div className="font-semibold text-[#1E3064]">{availabilityResult.address || 'Address found'}</div>
              </div>
              
              {/* Result summary */}
              {availabilityResult.has_fibre ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-500 p-2 rounded-full">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-700">Great News!</div>
                      <div className="text-green-600">High-speed fibre is available at your address</div>
                    </div>
                  </div>
                  
                  {/* Available speeds - unified view */}
                  <div className="bg-white rounded-lg p-4 mt-4">
                    <div className="text-sm text-slate-500 mb-3">Available speeds at your address:</div>
                    <div className="space-y-2">
                      {(() => {
                        // Collect all unique speeds from all providers
                        const speeds = new Set();
                        [availabilityResult.openeir, availabilityResult.siro, availabilityResult.nbi].forEach(provider => {
                          if (provider?.available && provider.products) {
                            provider.products.forEach(p => {
                              if (p.speed_mbps) speeds.add(p.speed_mbps);
                            });
                          }
                        });
                        const sortedSpeeds = [...speeds].sort((a, b) => a - b);
                        
                        return sortedSpeeds.map((speed, i) => (
                          <div key={speed} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Zap className={`w-5 h-5 ${i === sortedSpeeds.length - 1 ? 'text-[#F97530]' : 'text-[#1E3064]'}`} />
                              <span className="font-semibold text-[#1E3064]">
                                {speed >= 1000 ? `${speed/1000} Gbps` : `${speed} Mbps`}
                              </span>
                            </div>
                            {i === sortedSpeeds.length - 1 && (
                              <span className="text-xs bg-[#F97530] text-white px-2 py-1 rounded-full">Fastest</span>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-500 p-2 rounded-full">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-amber-700">Fibre Coming Soon</div>
                      <div className="text-amber-600">We'll notify you when service becomes available</div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <input 
                      type="email" 
                      placeholder="Enter your email to be notified"
                      className="w-full px-4 py-3 rounded-lg border border-amber-200 focus:outline-none focus:border-[#F97530]"
                    />
                  </div>
                </div>
              )}
              
              {/* CTA */}
              {availabilityResult.has_fibre && (
                <div className="space-y-3">
                  <button 
                    onClick={() => {
                      setShowResults(false);
                      setShowOrderForm(true);
                    }}
                    className="w-full bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white font-bold py-4 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    data-testid="get-started-btn"
                  >
                    <Rocket className="w-5 h-5" />
                    Get Started - Choose Your Package
                  </button>
                  <p className="text-center text-slate-500 text-sm">
                    No contracts • No hidden fees • No price hikes
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Network Partners Bar */}
      <section className="py-8 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-slate-500 text-sm font-medium">Powered by Ireland's fibre networks:</p>
            <NetworkLogos />
            <GuaranteedIrishLogo variant="colorNoTag" className="h-12" />
          </div>
        </div>
      </section>

      {/* Speed Demonstration Section - GOTCHA ELEMENT */}
      <section className="py-20 md:py-32 bg-[#1E3064] relative overflow-hidden">
        <DataStream />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white font-['Outfit'] mb-4">
              See the <span className="text-[#F97530]">Difference</span>
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              5Gbps isn't just a number. Experience what true speed feels like.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Speed Race - Left */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <SpeedRace />
            </motion.div>

            {/* Live Counter + Stats - Right */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8">
                <LiveDataCounter />
              </div>
              
              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="font-['JetBrains_Mono'] text-2xl font-bold text-[#F97530]">5Gb</div>
                  <div className="text-white/60 text-xs">Download</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="font-['JetBrains_Mono'] text-2xl font-bold text-[#F97530]">1Gb</div>
                  <div className="text-white/60 text-xs">Upload</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="font-['JetBrains_Mono'] text-2xl font-bold text-[#F97530]">&lt;5ms</div>
                  <div className="text-white/60 text-xs">Latency</div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* What this means section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { icon: Film, text: "4K movie", time: "40 seconds" },
              { icon: Gamepad2, text: "100GB game", time: "2.5 minutes" },
              { icon: Video, text: "1hr 4K video upload", time: "8 minutes" },
              { icon: Download, text: "Entire music library", time: "Instant" },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center">
                <item.icon className="w-8 h-8 text-[#F97530] mx-auto mb-2" />
                <div className="text-white font-medium text-sm">{item.text}</div>
                <div className="text-[#F97530] font-bold font-['JetBrains_Mono']">{item.time}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Promise - No Hidden Fees Banner */}
      <section className="py-12 bg-[#1E3064]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promiseBadges.map((badge, i) => (
              <motion.div
                key={badge.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-5"
              >
                <div className="bg-[#F97530] p-3 rounded-xl">
                  <badge.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-bold text-white text-lg">{badge.title}</div>
                  <div className="text-white/70 text-sm">{badge.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-4">
              {selectedType === "home" ? "Home" : "Business"} Broadband Packages
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
              {selectedType === "home" 
                ? "Simple, honest pricing. No hidden fees, no surprises."
                : "Enterprise-grade connectivity with dedicated Irish support."
              }
            </p>
            
            {/* Toggle */}
            <div className="flex items-center gap-2 p-1.5 bg-slate-100 rounded-full w-fit mx-auto">
              <button
                data-testid="pkg-toggle-home"
                onClick={() => setSelectedType("home")}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedType === "home" 
                    ? "bg-[#1E3064] text-white" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Home className="w-5 h-5" />
                Home
              </button>
              <button
                data-testid="pkg-toggle-business"
                onClick={() => setSelectedType("business")}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedType === "business" 
                    ? "bg-[#1E3064] text-white" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Building2 className="w-5 h-5" />
                Business
              </button>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedType}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center"
            >
              {packages.length === 0 ? (
                <div className="col-span-3 text-center py-12 text-slate-500">
                  Loading packages...
                </div>
              ) : packages.slice(0, 3).map((pkg, i) => {
                // Determine if this is the "popular" middle package
                const pkgName = pkg.display_name || pkg.name || '';
                const isPopular = i === 1 || pkgName.toLowerCase().includes('1000') || pkgName.toLowerCase().includes('pro');
                // Get speed from API or use old format
                const speed = pkg.download_speed_mbps || pkg.speed;
                // Get price from API or use old format
                const price = pkg.monthly_price || pkg.price;
                // Get features from API (array of objects or strings)
                const featuresList = pkg.features ? 
                  (typeof pkg.features[0] === 'object' && pkg.features[0]?.name ? pkg.features.map(f => f.name) : pkg.features) : [];
                
                return (
                <motion.div
                  key={pkg.id || pkgName}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`package-card-${i}`}
                  className={`relative rounded-3xl p-8 ${
                    isPopular 
                      ? "bg-[#1E3064] text-white shadow-2xl transform md:-translate-y-4 border border-[#F97530]/30" 
                      : "bg-white border border-slate-200 shadow-lg hover:shadow-xl transition-shadow"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white text-sm font-bold px-4 py-1.5 rounded-full">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 font-['Outfit'] ${isPopular ? 'text-white' : 'text-[#1E3064]'}`}>
                      {pkgName}
                    </h3>
                    <div className="flex items-center justify-center gap-2">
                      <Gauge className={`w-6 h-6 ${isPopular ? 'text-[#F97530]' : 'text-[#F97530]'}`} />
                      <div className={`font-['JetBrains_Mono'] text-5xl font-bold ${isPopular ? 'text-[#F97530]' : 'text-[#1E3064]'}`}>
                        {speed >= 1000 ? `${speed/1000}Gb` : `${speed}Mb`}
                      </div>
                    </div>
                    <div className={`text-sm ${isPopular ? 'text-white/70' : 'text-slate-500'}`}>per second</div>
                  </div>

                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={`text-4xl font-bold ${isPopular ? 'text-white' : 'text-[#1E3064]'}`}>€{price}</span>
                      <span className={`${isPopular ? 'text-white/70' : 'text-slate-500'}`}>/month</span>
                    </div>
                    <div className={`text-xs mt-1 ${isPopular ? 'text-white/60' : 'text-slate-400'}`}>excl. VAT</div>
                    {pkg.contract_length_months === 0 && (
                      <div className={`text-xs mt-1 ${isPopular ? 'text-[#F97530]' : 'text-green-600'}`}>No contract</div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {featuresList.slice(0, 4).map((feature, j) => (
                      <li key={j} className={`flex items-center gap-2 ${isPopular ? 'text-white/90' : 'text-slate-600'}`}>
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${isPopular ? 'text-[#F97530]' : 'text-green-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`get-started-btn-${i}`}
                    onClick={() => {
                      setSelectedPackageForOrder(pkg);
                      setShowOrderForm(true);
                    }}
                    className={`w-full py-4 px-6 rounded-full font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
                      isPopular
                        ? "bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white hover:shadow-lg"
                        : "bg-[#1E3064] text-white hover:bg-[#2a4080]"
                    }`}
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              )})}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Enterprise Connectivity Section - Only shows for Business */}
      {selectedType === "business" && (
        <section className="py-20 md:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-4">
                Enterprise Connectivity
              </h2>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto">
                For businesses that need more. Dedicated solutions for large organisations and data-intensive operations.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {enterpriseFeatures.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="bg-[#1E3064] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-[#F97530]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3064] mb-3 font-['Outfit']">{feature.title}</h3>
                  <p className="text-slate-600 mb-6">{feature.desc}</p>
                  <button className="text-[#F97530] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-slate-600 mb-4">Need a custom solution for your enterprise?</p>
              <button className="bg-[#1E3064] text-white font-bold py-4 px-8 rounded-full hover:bg-[#2a4080] transition-colors inline-flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Our Enterprise Team
              </button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Why Us Section */}
      <section id="why-us" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-4">
              Why Choose GeekBroadband?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto">
              We're not just another provider. We're your neighbours, committed to keeping Ireland connected.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`feature-card-${i}`}
                className="bg-[#F8FAFC] p-8 rounded-2xl border border-slate-100 hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                  feature.color === 'orange' 
                    ? 'bg-gradient-to-r from-[#F97530] to-[#FF9F5B]' 
                    : 'bg-[#1E3064]'
                }`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1E3064] mb-3 font-['Outfit']">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what real Irish customers think.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayTestimonials.slice(0, 3).map((testimonial, i) => (
              <motion.div
                key={testimonial.name || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100"
              >
                <Quote className="w-10 h-10 text-[#F97530]/20 mb-4" />
                <p className="text-slate-600 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <div>
                  <div className="font-bold text-[#1E3064]">{testimonial.name}</div>
                  <div className="text-slate-500 text-sm">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reforest Nation - Environmental Commitment */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-green-900 via-green-800 to-green-900 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/reforest-nation-logo.png"
                alt="Reforest Nation"
                className="h-16 md:h-20 mb-8 object-contain"
              />
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white font-['Outfit'] mb-6">
                Helping to Make
                <span className="text-green-400"> Ireland Wild Again</span>
              </h2>
              
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                We're proud to partner with Reforest Nation in their mission to restore Ireland's native woodlands. 
                For every new customer, we plant a tree in Ireland. Because fast broadband and a healthy planet 
                should go hand in hand.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="font-['JetBrains_Mono'] text-3xl font-bold text-green-400">1,000+</div>
                  <div className="text-white/70 text-sm">Trees planted so far</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="font-['JetBrains_Mono'] text-3xl font-bold text-green-400">Native</div>
                  <div className="text-white/70 text-sm">Irish species only</div>
                </div>
              </div>

              <a 
                href="https://www.reforestnation.ie" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-400 font-semibold hover:text-green-300 transition-colors"
              >
                Learn more about Reforest Nation <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Animated Woodland Restoration Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 bg-gradient-to-b from-transparent via-green-900/30 to-green-950/50 rounded-3xl overflow-hidden"
            >
              {/* Sky gradient */}
              <div className="absolute inset-0 bg-gradient-to-b from-green-800/20 to-transparent" />
              
              {/* Ground */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-green-950 to-green-900/80" />
              
              {/* Trees - Row 1 (Back, smaller) */}
              <div className="absolute bottom-12 left-0 right-0 flex justify-around items-end px-4">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <motion.div
                    key={`back-${i}`}
                    initial={{ scaleY: 0, opacity: 0 }}
                    whileInView={{ scaleY: 1, opacity: 0.5 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
                    className="flex flex-col items-center origin-bottom"
                  >
                    <div className="w-12 h-16 bg-green-600 rounded-full" />
                    <div className="w-2 h-6 bg-amber-800 -mt-2" />
                  </motion.div>
                ))}
              </div>
              
              {/* Trees - Row 2 (Middle) */}
              <div className="absolute bottom-10 left-0 right-0 flex justify-around items-end px-8">
                {[1, 2, 3, 4, 5].map((i) => (
                  <motion.div
                    key={`mid-${i}`}
                    initial={{ scaleY: 0, opacity: 0 }}
                    whileInView={{ scaleY: 1, opacity: 0.7 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + i * 0.15, duration: 0.8, ease: "easeOut" }}
                    className="flex flex-col items-center origin-bottom"
                  >
                    <div className="w-16 h-24 bg-green-500 rounded-full" />
                    <div className="w-3 h-8 bg-amber-700 -mt-3" />
                  </motion.div>
                ))}
              </div>
              
              {/* Trees - Row 3 (Front, largest) */}
              <div className="absolute bottom-8 left-0 right-0 flex justify-around items-end px-12">
                {[1, 2, 3].map((i) => (
                  <motion.div
                    key={`front-${i}`}
                    initial={{ scaleY: 0, opacity: 0 }}
                    whileInView={{ scaleY: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1 + i * 0.2, duration: 1, ease: "easeOut" }}
                    className="flex flex-col items-center origin-bottom"
                  >
                    <div className="w-20 h-32 bg-green-400 rounded-full shadow-lg" />
                    <div className="w-4 h-10 bg-amber-600 -mt-4 rounded-b" />
                  </motion.div>
                ))}
              </div>
              
              {/* Floating leaves */}
              {[...Array(15)].map((_, i) => (
                <motion.div
                  key={`leaf-${i}`}
                  className="absolute w-2 h-2 bg-green-300 rounded-full"
                  style={{ left: `${10 + (i * 6)}%`, bottom: '20%' }}
                  animate={{
                    y: [-20, -150 - Math.random() * 100],
                    x: [0, (Math.random() - 0.5) * 60],
                    opacity: [0, 1, 1, 0],
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 4 + Math.random() * 2,
                    repeat: Infinity,
                    delay: i * 0.4,
                    ease: "easeOut",
                  }}
                />
              ))}
              
              {/* Birds */}
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={`bird-${i}`}
                  className="absolute text-white/60 text-2xl"
                  style={{ top: `${15 + i * 8}%` }}
                  initial={{ left: '-10%', opacity: 0 }}
                  animate={{ 
                    left: '110%',
                    opacity: [0, 1, 1, 0],
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    delay: i * 3,
                    ease: "linear",
                  }}
                >
                  ∿
                </motion.div>
              ))}
              
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 2 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-green-500/30 backdrop-blur-sm px-6 py-3 rounded-full border border-green-400/30"
              >
                <Leaf className="w-5 h-5 text-green-400" />
                <span className="text-green-300 font-semibold text-sm">Restoring Native Irish Woodland</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Irish Identity Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${irishImages.countryside}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3064]/95 via-[#1E3064]/90 to-[#1E3064]/70" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <GuaranteedIrishLogo variant="white" className="h-20 mb-8" />
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white font-['Outfit'] mb-6">
                Proudly Irish,
                <span className="text-[#F97530]"> Fiercely Local</span>
              </h2>
              
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                We're a Guaranteed Irish company based in County Clare. When you call us, you'll talk to someone local 
                who understands your needs. No overseas call centres, just real Irish support.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="font-['JetBrains_Mono'] text-3xl font-bold text-[#F97530]">Clare</div>
                  <div className="text-white/70 text-sm">Based in County Clare</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="font-['JetBrains_Mono'] text-3xl font-bold text-[#F97530]">Local</div>
                  <div className="text-white/70 text-sm">Irish support team</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src={irishImages.aerial}
                alt="Beautiful Irish landscape"
                className="w-full h-80 object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-6">
              Ready to <span className="text-[#F97530]">Get Connected?</span>
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-10">
              Check if our blazing fast fibre is available at your address.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                data-testid="cta-check-btn"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center gap-2 text-lg"
              >
                <Rocket className="w-5 h-5" />
                Check Availability
              </button>
              <a
                href="tel:+353656712772"
                data-testid="cta-call-btn"
                className="bg-[#1E3064] text-white font-bold py-4 px-10 rounded-full hover:bg-[#2a4080] transition-all duration-300 flex items-center justify-center gap-2 text-lg"
              >
                <Phone className="w-5 h-5" />
                065 671 2772
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      {apiFaqs.length > 0 && (
        <section id="faq" className="py-20 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1E3064] font-['Outfit'] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600">
                Got questions? We've got answers.
              </p>
            </motion.div>

            <div className="space-y-4">
              {apiFaqs.map((faq, i) => (
                <motion.details
                  key={faq.id || i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group bg-slate-50 rounded-2xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none hover:bg-slate-100 transition-colors">
                    <span className="font-semibold text-[#1E3064] text-left pr-4">{faq.question}</span>
                    <span className="text-[#F97530] transform group-open:rotate-180 transition-transform">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer - Similar to old site */}
      <footer className="bg-[#1E3064] text-white">
        {/* Top footer with links */}
        <div className="py-16 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
              {/* Logo & description */}
              <div className="lg:col-span-2">
                <Logo className="h-12 brightness-0 invert mb-6" />
                <p className="text-white/70 mb-6 max-w-sm">
                  Ireland's friendliest broadband provider. Blazing fast fibre with genuine local support.
                </p>
                <div className="flex items-center gap-4 flex-wrap">
                  <GuaranteedIrishLogo variant="whiteNoTag" className="h-10" />
                  <img 
                    src="/images/comreg-logo.png"
                    alt="Registered with ComReg"
                    className="h-8"
                    title="Registered with the Commission for Communications Regulation"
                  />
                </div>
              </div>
              
              {/* Home Broadband */}
              <div>
                <h4 className="font-bold text-lg mb-4 font-['Outfit'] flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#F97530]" />
                  Home Broadband
                </h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">500Mb Essential</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">1Gb Family</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">2Gb Gigabit Pro</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">Check Availability</a></li>
                </ul>
              </div>
              
              {/* Business Broadband */}
              <div>
                <h4 className="font-bold text-lg mb-4 font-['Outfit'] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#F97530]" />
                  Business Broadband
                </h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">Business Starter</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">Business Pro</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">Business Ultra</a></li>
                  <li><a href="#" className="text-white/70 hover:text-[#F97530] transition-colors">Enterprise Solutions</a></li>
                </ul>
              </div>
              
              {/* Contact */}
              <div>
                <h4 className="font-bold text-lg mb-4 font-['Outfit']">Contact Us</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-white/70">
                    <MapPin className="w-4 h-4 text-[#F97530] mt-1 flex-shrink-0" />
                    <span>18 Henry Street, Kilrush,<br />Co. Clare V15 CX71</span>
                  </li>
                  <li className="flex items-center gap-2 text-white/70">
                    <Phone className="w-4 h-4 text-[#F97530]" />
                    065 671 2772
                  </li>
                  <li className="flex items-center gap-2 text-white/70">
                    <Phone className="w-4 h-4 text-[#F97530]" />
                    1800 665 500
                  </li>
                  <li className="flex items-center gap-2 text-white/70">
                    <Mail className="w-4 h-4 text-[#F97530]" />
                    hello@geekbroadband.ie
                  </li>
                </ul>
                <div className="mt-6">
                  <a href="/portal" className="bg-[#F97530] text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#e86620] transition-colors text-sm">
                    <Lock className="w-4 h-4" />
                    My Account
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Network partners */}
        <div className="py-8 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              <span className="text-white/50 text-sm">Network Partners:</span>
              <NetworkLogos inverted={true} />
            </div>
          </div>
        </div>
        
        {/* Company info & legal */}
        <div className="py-6 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-white/50 text-sm">
              <span>Company Reg: 741658</span>
              <span>VAT ID: IE4158538SH</span>
              <span className="flex items-center gap-2">
                <img 
                  src="/images/comreg-logo.png"
                  alt="ComReg"
                  className="h-4"
                />
                Registered
              </span>
            </div>
          </div>
        </div>
        
        {/* Bottom footer */}
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">© 2025 GeekBroadband. All rights reserved.</p>
            <div className="flex items-center flex-wrap justify-center gap-x-6 gap-y-2">
              <a href="/legal/privacy-policy" className="text-white/50 hover:text-[#F97530] text-sm transition-colors">Privacy Policy</a>
              <a href="/legal/terms-of-service" className="text-white/50 hover:text-[#F97530] text-sm transition-colors">Terms of Service</a>
              <a href="/legal/cookie-policy" className="text-white/50 hover:text-[#F97530] text-sm transition-colors">Cookie Policy</a>
              <a href="/legal/modern-slavery" className="text-white/50 hover:text-[#F97530] text-sm transition-colors">Modern Slavery & Human Trafficking Statement</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Order Form Modal */}
      {showOrderForm && (
        <OrderForm
          availabilityResult={availabilityResult}
          eircode={eircode}
          onClose={() => {
            setShowOrderForm(false);
            setSelectedPackageForOrder(null);
          }}
          preSelectedPackage={selectedPackageForOrder}
          serviceType={selectedType}
        />
      )}
    </div>
  );
}

// Legal Page Component
function LegalPage() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get slug from URL
  const slug = window.location.pathname.split('/legal/')[1];

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/legal/${slug}`);
        if (!res.ok) {
          throw new Error('Page not found');
        }
        const data = await res.json();
        setPage(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchPage();
    }
  }, [slug]);

  // Logo component for header
  const Logo = () => (
    <a href="/" className="flex items-center gap-2">
      <div className="text-2xl font-bold text-[#1E3064]">
        Geek<span className="text-[#F97530]">Broadband</span>
      </div>
    </a>
  );

  // Full footer matching main site
  const LegalFooter = () => (
    <footer className="bg-[#1E3064]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="text-3xl font-bold text-white mb-4">
              Geek<span className="text-[#F97530]">Broadband</span>
            </div>
            <p className="text-white/70 text-sm">
              Ireland's friendliest broadband provider. Local expertise, blazing fast speeds.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><a href="/#packages" className="text-white/70 hover:text-[#F97530] transition text-sm">Packages</a></li>
              <li><a href="/#check" className="text-white/70 hover:text-[#F97530] transition text-sm">Check Availability</a></li>
              <li><a href="/#faq" className="text-white/70 hover:text-[#F97530] transition text-sm">FAQs</a></li>
              <li><a href="/portal" className="text-white/70 hover:text-[#F97530] transition text-sm">My Account</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><a href="/legal/privacy-policy" className="text-white/70 hover:text-[#F97530] transition text-sm">Privacy Policy</a></li>
              <li><a href="/legal/terms-of-service" className="text-white/70 hover:text-[#F97530] transition text-sm">Terms of Service</a></li>
              <li><a href="/legal/cookie-policy" className="text-white/70 hover:text-[#F97530] transition text-sm">Cookie Policy</a></li>
              <li><a href="/legal/modern-slavery" className="text-white/70 hover:text-[#F97530] transition text-sm">Modern Slavery</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-white/70">
                <MapPin className="w-4 h-4 text-[#F97530]" />
                18 Henry Street, Kilrush, Co. Clare
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <Phone className="w-4 h-4 text-[#F97530]" />
                065 671 2772
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <Mail className="w-4 h-4 text-[#F97530]" />
                hello@geekbroadband.ie
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Copyright */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} GeekBroadband. All rights reserved.
            </p>
            <p className="text-white/50 text-sm">
              Regulated by ComReg | Borrow A Geek Limited trading as GeekBroadband
            </p>
          </div>
        </div>
      </div>
    </footer>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1E3064] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        {/* Header with Logo */}
        <header className="bg-white border-b border-slate-100 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <Logo />
            <a href="/" className="flex items-center gap-2 text-[#1E3064] hover:text-[#F97530] text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Home
            </a>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#1E3064] mb-4">Page Not Found</h1>
            <p className="text-slate-600">The page you're looking for doesn't exist.</p>
          </div>
        </main>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header with Logo */}
      <header className="bg-white border-b border-slate-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Logo />
          <a href="/" className="flex items-center gap-2 text-[#1E3064] hover:text-[#F97530] text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Home
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        <h1 className="text-4xl font-bold text-[#1E3064] mb-2 font-['Outfit']">{page.title}</h1>
        {page.last_updated && (
          <p className="text-slate-500 text-sm mb-8">Last updated: {new Date(page.last_updated).toLocaleDateString('en-IE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        )}
        <div 
          className="prose prose-slate max-w-none prose-headings:text-[#1E3064] prose-a:text-[#F97530]"
          dangerouslySetInnerHTML={{ __html: page.content }}
        />
      </main>

      {/* Full Footer */}
      <LegalFooter />
    </div>
  );
}

// Main App with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="/portal/*" element={<CustomerPortal />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/*" element={<MainSite />} />
      </Routes>
    </Router>
  );
}

export default App;
